import { SERVICE_TABS, SERVICE_CARD_TEXTS, TZB_URL } from "appLauncher";
import { useState, useRef, useLayoutEffect } from "react";
import styled from "styled-components";
import { SectionTitle, SectionSubtitle } from "components/Style";

const ServicesSection = styled.section`
  padding: 66px 0;
  background-color: #fff;
`;

const ServicesContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ServiceTabs = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #e9ecef;
  border-radius: 999px;
  padding: 14px;
  width: fit-content;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin: 34px auto 44px auto;
`;

const TabSlider = styled.div`
  position: absolute;
  top: 14px;
  left: 0;
  height: calc(100% - 28px);
  background: #007bff;
  border-radius: 999px;
  z-index: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const ServiceTab = styled.div<{ $active: boolean }>`
  padding: 8px 28px;
  margin: 0 7px;
  background-color: ${(props) => (props.$active ? "#007bff" : "transparent")};
  color: ${(props) => (props.$active ? "#fff" : "#1B2532")};
  border-radius: 999px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.3s, color 0.3s;
  position: relative;
  z-index: 1;
  box-shadow: none;
  &:hover {
    ${(props) =>
      props.$active
        ? ""
        : `background-color: rgba(51, 150, 255, 0.18); color: #007bff;`}
  }
`;

const ServiceCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 40px;
  justify-content: center;
  place-items: center;
`;

const ServiceCard = styled.div<{ $highlight?: boolean }>`
  background: ${({ $highlight }) =>
    $highlight
      ? "linear-gradient(-45deg, #1A4FFE 0%, #5C9DFF 100%)"
      : "linear-gradient(-45deg, #F0F4FD 0%, #FFFFFF 60%, #F0F4FD 100%)"};
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s, background 0.3s;
  width: 350px;
  height: 400px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }

  ${(props) =>
    props.$highlight &&
    `
    color: #fff;
    h3, p, span, a, div, svg, path {
      color: #fff !important;
      fill: #fff !important;
      stroke: #fff !important;
    }
    button {
      color: #2640ed !important;
      background: #fff !important;
      border: none !important;
      box-shadow: none !important;
    }
  `}
`;

const CardMoreBtn = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  height: 28px;
  min-width: 36px;
  padding: 0 10px;
  background: #fff;
  border: 1.5px solid #2640ed;
  border-radius: 14px;
  color: #2640ed;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s, border 0.2s;
  z-index: 2;
  box-shadow: none;
  line-height: 28.5px;
  font-weight: 500;
  &:hover {
    background: #f5f7fa;
    border-color: #1a2e99;
    color: #1a2e99;
  }
`;

const CardBottom = styled.div`
  margin-top: auto;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`;

const CardTitle = styled.h3`
  margin-bottom: 6px;
  font-weight: bold;
`;

const CardTag = styled.span<{ $highlight?: boolean }>`
  display: inline-flex;
  align-items: center;
  background: ${({ $highlight }) =>
    $highlight ? "rgba(255,255,255,0.16)" : "rgba(253, 134, 22, 0.16)"};
  color: ${({ $highlight }) => ($highlight ? "#fff" : "#fd8616")};
  font-size: 13px;
  font-weight: 500;
  border-radius: 8px;
  padding: 2px 12px 2px 8px;
  margin-bottom: 8px;
  margin-top: 0;
  width: auto;
`;

const CardText = styled.p`
  font-size: 15px;
  line-height: 2;
  margin-bottom: 10px;
  color: #666;
`;

const ApplyServiceLink = styled.a`
  text-decoration: none;
  color: #2640ed;
  transition: color 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 14px;

  &:hover {
    color: #1a2e99;
    svg {
      transform: translateX(2px);
    }
  }

  svg {
    transition: transform 0.2s;
    display: inline-block;
    vertical-align: middle;
  }
`;

export default function Services() {
  const [activeTab, setActiveTab] = useState(0);
  const [activeCard, setActiveCard] = useState(0);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tabsBarRef = useRef<HTMLDivElement>(null);
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0 });
  const [showSlider, setShowSlider] = useState(true);
  const cards = SERVICE_TABS[activeTab]?.cards || [];

  useLayoutEffect(() => {
    if (tabRefs.current[activeTab] && tabsBarRef.current) {
      const tab = tabRefs.current[activeTab];
      const bar = tabsBarRef.current;
      const tabRect = tab!.getBoundingClientRect();
      const barRect = bar.getBoundingClientRect();
      setSliderStyle({
        left: tabRect.left - barRect.left,
        width: tabRect.width,
      });
      setShowSlider(true);
    }
  }, [activeTab, SERVICE_TABS.length]);

  // 切换Tab时显示Slider
  const handleTabClick = (index: number) => {
    setShowSlider(true);
    setActiveTab(index);
  };

  // 切换Tab时重置卡片选中
  useLayoutEffect(() => {
    setActiveCard(0);
  }, [activeTab]);

  // TabSlider动画结束后隐藏
  const handleSliderTransitionEnd = () => {
    setShowSlider(false);
  };

  return (
    <ServicesSection>
      <ServicesContainer>
        <SectionTitle>全流程线上一站式办理7*24小时 服务不打烊</SectionTitle>
        <SectionSubtitle>One Click Direct Guarantee Letter</SectionSubtitle>
        <ServiceTabs ref={tabsBarRef}>
          {showSlider && (
            <TabSlider
              style={{ left: sliderStyle.left, width: sliderStyle.width }}
              onTransitionEnd={handleSliderTransitionEnd}
              onTransitionStart={() => setShowSlider(true)}
            />
          )}
          {SERVICE_TABS.map((tab, index) => (
            <ServiceTab
              key={index}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              $active={index === activeTab}
              onClick={() => handleTabClick(index)}
              style={{ zIndex: 1 }}
            >
              {tab.label}
            </ServiceTab>
          ))}
        </ServiceTabs>
        <ServiceCards>
          {cards.length === 0 ? (
            <div
              style={{
                gridColumn: "1/-1",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                minHeight: 124,
              }}
            >
              <img
                src="/assets/home/empty.png"
                alt="暂无数据"
                width={158}
                height={124}
                style={{ objectFit: "contain", opacity: 0.7 }}
              />
              <div>产品更新中</div>
            </div>
          ) : (
            cards.map((title, idx) => (
              <ServiceCard
                key={title}
                $highlight={activeCard === idx}
                onClick={() => setActiveCard(idx)}
              >
                <CardMoreBtn onClick={() => (location.href = "/product")}>
                  更多
                </CardMoreBtn>
                <CardTitle>{title}</CardTitle>
                <div style={{ width: "fit-content" }}>
                  <CardTag $highlight={activeCard === idx}>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4 2h5l-2 4h3L6 12v-4H3l1-6z"
                        fill={activeCard === idx ? "#fff" : "#FD8616"}
                      />
                    </svg>
                    秒出函
                  </CardTag>
                </div>
                <CardText>{SERVICE_CARD_TEXTS[title] || ""}</CardText>
                <CardBottom>
                  <ApplyServiceLink
                    onClick={() => window.open(TZB_URL, "_blank")}
                  >
                    在线申请
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7 4l4 5-4 5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ApplyServiceLink>
                  <img
                    src={"/assets/home/type-" + (idx + 1) + ".png"}
                    alt="icon"
                    width={84}
                    height={84}
                    style={{ objectFit: "contain" }}
                  />
                </CardBottom>
              </ServiceCard>
            ))
          )}
        </ServiceCards>
      </ServicesContainer>
    </ServicesSection>
  );
}
